import _ from 'lodash';
import React, {FC} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {VirtualTableSortableColumnDirectionsEnum} from '../../../../../../../../src/components/common/table/helpers/tableHelpers';
import {convertDynamicColumnToColumn} from '../../../../../../../../src/components/common/table/tableColumn';
import {CancelToken} from '../../../../../../../../src/helpers/asyncCancelHelpers';
import {ResolveDispatchProps} from '../../../../../../../../src/helpers/reduxHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../../../src/helpers/reselectHelpers';
import {getCustomFieldsForAccountsFlat} from '../../../../../../../../src/store/data/customFields/customFieldsSelectors';
import {
  getContactsManagerTableState,
  makeGetAccountId,
  makeGetAccountsTableAccountIds,
  makeGetContactsManagerTableTokenPaginationData,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/contactsManagerPanelSelectors';
import {
  closeSidePanelAction,
  openCreateAccountFormAction,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/sidePanel/contactsManagerSidePanelActions';
import {
  contactsManagerFetchAccountsTableDataAsyncAction,
  contactsManagerSetUpAccountsTableAction,
  contactsManagerTableCleanupAction,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableActions';
import {tableStateIsAccountsTableState} from '../../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableState';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {navigateToLinkAction} from '../../../../../../../../src/store/stateless/navigationActions';
import {compareName} from '../../../../../../../../srcCommon/helpers/localeHelpers';
import {useInitialEffect} from '../../../../../../../shared/helpers/src/react/hooks/useEffectHelpers';
import {Renderer} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {
  AccountsTableColumnsEnum,
  getDynamicColumnConfigFromCustomFieldType,
} from '../common/accounts/accountsTableConfig';
import {ContactsManagerCustomFieldColumnMetadata} from '../common/contactsManagerCustomFieldTypes';
import {AccountsTable} from './accountsTable';

/*
 * Props.
 */

interface AccountsTableContainerPublicProps {
  panelId: ContactsManagerPanelId;
  renderImportButton: Renderer;
}
type AccountsTableContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type AccountsTableContainerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type AccountsTableContainerComponentProps = AccountsTableContainerPublicProps &
  AccountsTableContainerStateProps &
  AccountsTableContainerDispatchProps;

/*
 * Selectors.
 */

function makeGetDynamicColumns() {
  return createSelector([getCustomFieldsForAccountsFlat], (customFields) =>
    _([...customFields])
      .sort((customField1, customField2) => compareName(customField1.name, customField2.name))
      .map((customField) => {
        const columnConfig = getDynamicColumnConfigFromCustomFieldType(customField.type);
        if (!columnConfig) {
          return undefined;
        }

        return convertDynamicColumnToColumn<
          AccountsTableColumnsEnum,
          ContactsManagerCustomFieldColumnMetadata
        >(`${customField.id}`, customField.name, columnConfig, {
          customFieldType: customField.type,
        });
      })
      .compact()
      .value(),
  );
}

function makeGetAccountsTableStatus() {
  return createSelector([getContactsManagerTableState], (tableState) => {
    if (!tableState || !tableStateIsAccountsTableState(tableState)) {
      return undefined;
    }
    return tableState.status;
  });
}

/*
 * Component.
 */

export const AccountsTableContainerComponent: FC<AccountsTableContainerComponentProps> = (props) => {
  const {
    panelId,
    selectedAccountId,
    accountIds,
    dynamicColumns,
    paginationData,
    tableStatus,
    onFetchTableDataAsync,
    setUpAccountsTable,
    cleanup,
    navigateToLink,
    openCreateAccountForm,
    renderImportButton,
    closeSidePanel,
  } = props;

  useInitialEffect(() => {
    setUpAccountsTable();

    // Listen for the component to be unmounted and clean it up.
    return () => {
      cleanup();
      closeSidePanel();
    };
  });

  const onReset = () => {
    cleanup();
    closeSidePanel();
    setUpAccountsTable();
  };

  return (
    <AccountsTable
      panelId={panelId}
      tableStatus={tableStatus}
      selectedAccountId={selectedAccountId}
      accountIds={accountIds}
      dynamicColumns={dynamicColumns}
      paginationData={paginationData}
      onFetchTableDataAsync={onFetchTableDataAsync}
      onReset={onReset}
      navigateToLink={navigateToLink}
      openCreateAccountForm={openCreateAccountForm}
      renderImportButton={renderImportButton}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<AccountsTableContainerPublicProps>()(() => ({
  tableStatus: makeGetAccountsTableStatus(),
  selectedAccountId: makeGetAccountId(),
  accountIds: makeGetAccountsTableAccountIds(),
  dynamicColumns: makeGetDynamicColumns(),
  paginationData: makeGetContactsManagerTableTokenPaginationData(getContactsManagerTableState),
}));

const mapDispatchToProps = makeMapDispatchToProps<AccountsTableContainerPublicProps>()(({panelId}) => ({
  setUpAccountsTable: contactsManagerSetUpAccountsTableAction,
  cleanup: contactsManagerTableCleanupAction,
  closeSidePanel: closeSidePanelAction,
  navigateToLink: navigateToLinkAction,
  openCreateAccountForm: openCreateAccountFormAction,
  onFetchTableDataAsync: (
    cancelToken: CancelToken,
    sortBy?: string,
    sortOrder?: VirtualTableSortableColumnDirectionsEnum,
  ) => contactsManagerFetchAccountsTableDataAsyncAction(panelId, cancelToken, sortBy, sortOrder),
}));

export const AccountsTableContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(AccountsTableContainerComponent);
